import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API_URL, dashboardAPI, notificationsAPI } from '../../services/api';
import { TrendingUp, Package, DollarSign, ShoppingBag, Store, AlertCircle,
         Gem, Users, ArrowLeftRight, LayoutGrid, ChevronRight, Boxes,
         FileText, Wrench, History, Bell, AlertTriangle, PackageX,
         Clock, Wallet, X, BarChart3 } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Legend
} from 'recharts';

// ─── BOUTIQUE LANDING ────────────────────────────────────────────────────────
function BoutiqueDashboard({ context, navigate }) {
  const [stockIci, setStockIci] = useState('…');
  const boutiqueName = context.boutique_nom || 'Boutique';
  const boutiqueId   = context.boutique_id;

  useEffect(() => {
    if (!boutiqueId) return;
    fetch(`${API_URL}/bijoux/catalogue/modeles`)
      .then(r => r.json())
      .then(data => {
        if (!Array.isArray(data)) return;
        let n = 0;
        data.forEach(m => (m.pieces || []).forEach(p => {
          if (p.inventaire_actif && p.boutique_id === boutiqueId) n++;
        }));
        setStockIci(n);
      })
      .catch(() => setStockIci('—'));
  }, [boutiqueId]);

  const tiles = [
    {
      label: 'Vente Bijoux',
      sub: 'Catalogue & encaissement',
      icon: ShoppingBag,
      path: '/vente-boutique',
      grad: 'from-violet-600 to-purple-700',
      accent: 'bg-violet-100 text-violet-700',
    },
    {
      label: 'Clients',
      sub: 'Fiches & historique',
      icon: Users,
      path: '/clients',
      grad: 'from-sky-500 to-blue-700',
      accent: 'bg-sky-100 text-sky-700',
    },
    {
      label: 'Stock',
      sub: `${stockIci} pièces ici`,
      icon: Boxes,
      path: '/stocks',
      grad: 'from-emerald-500 to-teal-700',
      accent: 'bg-emerald-100 text-emerald-700',
    },
    {
      label: 'Catalogue',
      sub: 'Tous les modèles',
      icon: Gem,
      path: '/catalogue-modeles',
      grad: 'from-amber-500 to-orange-600',
      accent: 'bg-amber-100 text-amber-700',
    },
    {
      label: 'Ventes',
      sub: 'Historique & stats',
      icon: TrendingUp,
      path: '/ventes',
      grad: 'from-rose-500 to-pink-700',
      accent: 'bg-rose-100 text-rose-700',
    },
    {
      label: 'Transferts',
      sub: 'Inter-boutiques',
      icon: ArrowLeftRight,
      path: '/transferts',
      grad: 'from-indigo-500 to-blue-800',
      accent: 'bg-indigo-100 text-indigo-700',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero header */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white px-6 pt-10 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 70% 50%, white 1px, transparent 1px)', backgroundSize: '24px 24px' }}
        />
        <div className="relative max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-sm mb-4">
            <Store className="h-4 w-4" />
            Espace boutique
          </div>
          <h1 className="text-2xl md:text-4xl font-bold mb-2">{boutiqueName}</h1>
          <p className="text-gray-400 text-lg">Bienvenue — que voulez-vous faire ?</p>
        </div>
      </div>

      {/* Tiles grid */}
      <div className="max-w-4xl mx-auto px-6 -mt-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tiles.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.path}
                onClick={() => navigate(t.path)}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-200 text-left overflow-hidden group"
              >
                <div className={`h-2 bg-gradient-to-r ${t.grad}`} />
                <div className="p-5 flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl ${t.accent} flex items-center justify-center shrink-0`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-gray-800 text-base">{t.label}</div>
                    <div className="text-sm text-gray-500 mt-0.5">{t.sub}</div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-gray-500 transition-colors shrink-0" />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── COULEURS POUR PIE CHART ──────────────────────────────────────────────
const PIE_COLORS = ['#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#6366f1', '#14b8a6'];

// ─── ICON MAP POUR NOTIFICATIONS ──────────────────────────────────────────
const NOTIF_ICONS = {
  PackageX: PackageX,
  AlertTriangle: AlertTriangle,
  ArrowLeftRight: ArrowLeftRight,
  Clock: Clock,
  Wallet: Wallet,
};

// ─── NOTIFICATIONS BELL ──────────────────────────────────────────────────
function NotificationsBell() {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    notificationsAPI.getAll()
      .then(res => setNotifications(res.data || []))
      .catch(() => setNotifications([]));
  }, []);

  // Fermer le dropdown si clic en dehors
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const totalCount = notifications.reduce((s, n) => s + (n.count || 0), 0);
  const typeColors = { danger: 'bg-red-50 border-red-200 text-red-800', warning: 'bg-amber-50 border-amber-200 text-amber-800', info: 'bg-blue-50 border-blue-200 text-blue-800' };
  const dotColors = { danger: 'bg-red-500', warning: 'bg-amber-500', info: 'bg-blue-500' };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
      >
        <Bell className="h-6 w-6 text-gray-600" />
        {totalCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1">
            {totalCount > 99 ? '99+' : totalCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 w-80 md:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
            <h4 className="font-bold text-gray-800 text-sm">Notifications</h4>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-400 text-sm">Aucune alerte</div>
            ) : (
              notifications.map((notif, i) => {
                const Icon = NOTIF_ICONS[notif.icon] || AlertCircle;
                return (
                  <button
                    key={i}
                    onClick={() => { navigate(notif.link); setOpen(false); }}
                    className={`w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-gray-50 transition-colors flex items-center gap-3`}
                  >
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColors[notif.type] || 'bg-gray-400'}`} />
                    <Icon className="h-5 w-5 flex-shrink-0 opacity-60" />
                    <span className="text-sm flex-1">{notif.message}</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${typeColors[notif.type] || 'bg-gray-100 text-gray-600'}`}>
                      {notif.count}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CHARTS SECTION ──────────────────────────────────────────────────────
function ChartsSection() {
  const [caMensuel, setCaMensuel] = useState([]);
  const [categories, setCategories] = useState([]);
  const [boutiques, setBoutiques] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      dashboardAPI.chartsCaMensuel().then(r => r.data).catch(() => []),
      dashboardAPI.chartsCategories().then(r => r.data).catch(() => []),
      dashboardAPI.chartsBoutiques().then(r => r.data).catch(() => []),
    ]).then(([ca, cat, bout]) => {
      setCaMensuel(ca);
      setCategories(cat);
      setBoutiques(bout);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="text-center py-6 text-gray-400">Chargement des graphiques...</div>;
  }

  // Formater les mois pour affichage court
  const caData = caMensuel.map(r => ({
    ...r,
    label: r.mois ? r.mois.substring(5) + '/' + r.mois.substring(2, 4) : r.mois,
    total: r.officiel + r.instance
  }));

  const formatDH = (v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v.toLocaleString('fr-MA');

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
        <BarChart3 className="h-6 w-6 text-indigo-600" />
        Analyses
      </h3>

      {/* CA Evolution - Line Chart */}
      {caData.length > 0 && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <h4 className="font-bold text-gray-700 mb-4">Evolution du CA (12 derniers mois)</h4>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={caData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={formatDH} tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(value) => [`${Number(value).toLocaleString('fr-MA')} DH`]}
                labelFormatter={(label) => `Mois: ${label}`}
              />
              <Legend />
              <Line type="monotone" dataKey="officiel" name="CA Officiel" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="instance" name="CA Instance" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Categories - Pie Chart */}
        {categories.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <h4 className="font-bold text-gray-700 mb-4">Repartition par categorie</h4>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={categories}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {categories.map((_, idx) => (
                    <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value} vente(s)`]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Boutiques - Bar Chart */}
        {boutiques.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <h4 className="font-bold text-gray-700 mb-4">CA par boutique</h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={boutiques} layout="vertical" margin={{ top: 5, right: 20, left: 80, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="text" inputMode="decimal" onFocus={e => e.target.select()} tickFormatter={formatDH} tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={70} />
                <Tooltip formatter={(value) => [`${Number(value).toLocaleString('fr-MA')} DH`, 'CA']} />
                <Bar dataKey="value" name="CA" fill="#8b5cf6" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const { user: authUser } = useAuth();
  const ctx = (() => {
    try { return JSON.parse(localStorage.getItem('workspaceContext') || '{}'); }
    catch { return {}; }
  })();
  const isBoutique = ctx.type === 'boutique';
  const isAtelier = ctx.type === 'atelier';
  const isChefAtelier = isAtelier && (authUser?.role === 'atelier' || authUser?.role === 'chef_atelier');

  useEffect(() => {
    if (isBoutique) return;
    const loadData = async () => {
      try {
        const response = await dashboardAPI.global();
        setStats(response.data);
      } catch (error) {
        console.error('Erreur:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [isBoutique]);

  if (isBoutique) {
    return <BoutiqueDashboard context={ctx} navigate={navigate} />;
  }

  // Atelier : dashboard production sans infos financières
  if (isAtelier) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-800">Atelier de Production</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { path: '/devis', label: 'Devis', desc: 'Créer et gérer les devis', icon: FileText, color: 'bg-blue-500' },
            { path: '/atelier', label: 'Production', desc: 'Ordres de fabrication en cours', icon: Wrench, color: 'bg-amber-500' },
            { path: '/ouvriers', label: 'Ouvriers', desc: 'Gestion des ouvriers et tâches', icon: Users, color: 'bg-green-500' },
            ...(!isChefAtelier ? [
              { path: '/bijoux', label: 'Bijoux', desc: 'Catalogue des bijoux', icon: Gem, color: 'bg-purple-500' },
            ] : []),
            { path: '/historique-taches', label: 'Historique', desc: 'Historique des tâches réalisées', icon: History, color: 'bg-gray-500' },
            ...(!isChefAtelier ? [
              { path: '/tracabilite', label: 'Traçabilité', desc: 'Suivi complet des pièces', icon: History, color: 'bg-indigo-500' },
            ] : []),
          ].map(item => (
            <div key={item.path} onClick={() => navigate(item.path)}
              className="bg-white rounded-xl shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow border border-gray-100">
              <div className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center mb-4`}>
                <item.icon className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold text-gray-800 text-lg">{item.label}</h3>
              <p className="text-sm text-gray-500 mt-1">{item.desc}</p>
            </div>
          ))}
        </div>
        {stats && stats.transferts_en_cours > 0 && (
          <div onClick={() => navigate('/transferts')}
            className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center space-x-3 cursor-pointer hover:bg-yellow-100">
            <AlertCircle className="h-6 w-6 text-yellow-600" />
            <p className="font-semibold text-yellow-800">{stats.transferts_en_cours} transfert(s) en attente</p>
          </div>
        )}
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, color, suffix = '' }) => (
    <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs md:text-sm text-gray-600 mb-1 truncate">{title}</p>
          <p className="text-lg md:text-3xl font-bold text-gray-800 truncate">
            {typeof value === 'number' ? value.toLocaleString('fr-MA') : value}
            {suffix}
          </p>
        </div>
        <div className={`p-2 md:p-3 rounded-full ${color} flex-shrink-0`}>
          <Icon className="h-5 w-5 md:h-8 md:w-8 text-white" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  if (!stats) {
    return <div className="text-center py-8">Aucune donnée disponible</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-2">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-800">Tableau de bord global</h2>
        <NotificationsBell />
      </div>

      {/* ── Résumé du jour ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
        <StatCard title="CA Aujourd'hui" value={stats.ca_jour} icon={DollarSign} color="bg-green-500" suffix=" DH" />
        <StatCard title="Ventes Aujourd'hui" value={stats.ventes_jour} icon={ShoppingBag} color="bg-blue-500" />
        <StatCard title="CA ce mois" value={stats.ca_mois} icon={TrendingUp} color="bg-purple-500" suffix=" DH" />
        <StatCard title="Ventes ce mois" value={stats.ventes_mois} icon={ShoppingBag} color="bg-orange-500" />
      </div>

      {/* ── Double caisse : Officielle vs Instance ── */}
      {stats.stats_caisses && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Caisse Officielle */}
          <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-blue-500">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <h3 className="font-bold text-gray-800">Caisse Officielle</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 uppercase">Aujourd'hui</p>
                <p className="text-2xl font-bold text-blue-700">
                  {(stats.stats_caisses.aujourd_hui?.officielle?.ca || 0).toLocaleString('fr-MA')} DH
                </p>
                <p className="text-xs text-gray-500">{stats.stats_caisses.aujourd_hui?.officielle?.nb_ventes || 0} vente(s)</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Ce mois</p>
                <p className="text-2xl font-bold text-blue-600">
                  {(stats.stats_caisses.mois?.officielle?.ca || 0).toLocaleString('fr-MA')} DH
                </p>
                <p className="text-xs text-gray-500">{stats.stats_caisses.mois?.officielle?.nb_ventes || 0} vente(s)</p>
              </div>
            </div>
          </div>
          {/* Caisse Instance */}
          <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-amber-500">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-3 h-3 rounded-full bg-amber-500" />
              <h3 className="font-bold text-gray-800">En Instance</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 uppercase">Aujourd'hui</p>
                <p className="text-2xl font-bold text-amber-600">
                  {((stats.stats_caisses.aujourd_hui?.instance?.ca || 0) + (stats.stats_caisses.aujourd_hui?.noir?.ca || 0)).toLocaleString('fr-MA')} DH
                </p>
                <p className="text-xs text-gray-500">{(stats.stats_caisses.aujourd_hui?.instance?.nb_ventes || 0) + (stats.stats_caisses.aujourd_hui?.noir?.nb_ventes || 0)} vente(s)</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Ce mois</p>
                <p className="text-2xl font-bold text-amber-500">
                  {((stats.stats_caisses.mois?.instance?.ca || 0) + (stats.stats_caisses.mois?.noir?.ca || 0)).toLocaleString('fr-MA')} DH
                </p>
                <p className="text-xs text-gray-500">{(stats.stats_caisses.mois?.instance?.nb_ventes || 0) + (stats.stats_caisses.mois?.noir?.nb_ventes || 0)} vente(s)</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Stock et totaux ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
        <StatCard title="Stock total" value={stats.stock_total} icon={Package} color="bg-indigo-500" suffix=" pcs" />
        <StatCard title="Valeur du stock" value={stats.valeur_stock} icon={DollarSign} color="bg-teal-500" suffix=" DH" />
        <StatCard title="CA Total" value={stats.ca_total} icon={TrendingUp} color="bg-pink-500" suffix=" DH" />
        <StatCard title="Total ventes" value={stats.nb_ventes} icon={ShoppingBag} color="bg-cyan-500" />
      </div>

      {stats.transferts_en_cours > 0 && (
        <div 
          onClick={() => navigate('/transferts')}
          className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center space-x-3 cursor-pointer hover:bg-yellow-100 transition-colors"
        >
          <AlertCircle className="h-6 w-6 text-yellow-600" />
          <div>
            <p className="font-semibold text-yellow-800">
              {stats.transferts_en_cours} transfert(s) en attente de validation
            </p>
            <p className="text-sm text-yellow-700">Cliquez pour accéder aux transferts</p>
          </div>
        </div>
      )}

      {stats.stock_par_boutique && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center space-x-2">
            <Store className="h-6 w-6" />
            <span>Stock par boutique</span>
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Boutique</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantité</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Valeur (DH)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {stats.stock_par_boutique.map((boutique, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{boutique.nom}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 capitalize">{boutique.type}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900">{boutique.quantite}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                      {parseFloat(boutique.valeur).toLocaleString('fr-MA')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {stats.ventes_par_boutique && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Ventes par boutique</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Boutique</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Nb ventes</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">CA (DH)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {stats.ventes_par_boutique.map((boutique, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{boutique.nom}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900">{boutique.nb_ventes}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                      {parseFloat(boutique.ca).toLocaleString('fr-MA')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {stats.ventes_recentes && stats.ventes_recentes.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Ventes récentes (30 derniers jours)</h3>
          <div className="space-y-2">
            {stats.ventes_recentes.slice(0, 10).map((vente, index) => (
              <div key={index} className="flex flex-wrap justify-between items-center py-2 border-b last:border-b-0 gap-1">
                <span className="text-gray-600">{vente.date}</span>
                <div className="text-right">
                  <span className="font-semibold text-gray-800">{parseFloat(vente.ca).toLocaleString('fr-MA')} DH</span>
                  <span className="text-sm text-gray-500 ml-2">({vente.nb_ventes} vente{vente.nb_ventes > 1 ? 's' : ''})</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {stats.top_bijoux && stats.top_bijoux.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center space-x-2">
            <TrendingUp className="h-6 w-6 text-purple-600" />
            <span>Top 5 bijoux les plus vendus</span>
          </h3>
          <div className="space-y-3">
            {stats.top_bijoux.map((bijou, index) => (
              <div key={index} className="flex flex-wrap items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-100 gap-2">
                <div className="flex items-center space-x-3 min-w-0">
                  <div className="flex items-center justify-center w-8 h-8 bg-purple-600 text-white rounded-full font-bold flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="min-w-0">
                    <span className="font-semibold text-gray-900 text-sm md:text-base block truncate">{bijou.nom}</span>
                    <div className="flex items-center space-x-2 text-xs md:text-sm text-gray-600">
                      <span className="bg-purple-100 px-2 py-0.5 rounded truncate">{bijou.reference}</span>
                      <span className="capitalize hidden sm:inline">{bijou.categorie}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-bold text-sm md:text-lg text-purple-600">{parseFloat(bijou.ca).toLocaleString('fr-MA')} DH</div>
                  <div className="text-xs md:text-sm text-gray-500">{bijou.nb_ventes} vente{bijou.nb_ventes > 1 ? 's' : ''}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Analyses graphiques ── */}
      <ChartsSection />
    </div>
  );
}

export default Dashboard;
